// src/app/components/cards/CardPeekDialog.tsx
//
// Dialog somente-leitura com o detalhe COMPLETO de um card (descrição,
// checklist, comentários, anexos) — extraído de ChatSharedCardBubble.tsx
// pra ser reaproveitado em qualquer lugar que precise de um "peek" rápido
// de card sem sair da tela atual. Duas origens hoje:
//   - Chat interno (ChatSharedCardBubble): card compartilhado numa mensagem
//   - Central de Atendimento (TimelineEntryRow, shared.tsx; ContactProfile):
//     chip "Solicitação criada" na timeline / aba "Solicitações" do contato
//
// Layout espelha de propósito o drawer grande do board (CardDetailPanelV2:
// !max-w-[1200px] !w-[92vw] h-[88vh], coluna principal + sidebar de
// comentários) — mesma hierarquia visual (ícone+título de seção, barra de
// progresso da checklist, bolha de comentário) de CardMainColumn.tsx/
// CardChecklistSection.tsx/CardActivitySidebar.tsx — mas SEM importar esses
// componentes: eles mutam via boardId/cardId (IsBoardMember, não
// mirror-aware) e este dialog é somente leitura por design. Duplicar um
// punhado de classes Tailwind aqui é mais barato e mais seguro do que dar a
// esses componentes um modo readOnly.
//
// Quem chama decide de onde vêm os dados (fetchDetail) e o gate de acesso
// já é responsabilidade do endpoint chamado — este componente só interpreta
// 403/404/erro genérico da resposta.

import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import { useNavigate } from 'react-router';
import { renderBoldText } from '../../utils/renderBoldText';
import {
  AlertTriangle, AlignLeft, ArrowUpCircle, CalendarClock, CheckSquare, ExternalLink,
  Flag, Layout, Loader2, Lock, MessageSquareText, Paperclip, Square, SquareCheck,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ApiError } from '@/app/lib/api';
import type { CardDetailData, UserMini } from '@/app/pages/board-completo/card-detail/types';

export interface CardPeekExtraAction {
  label: string;
  icon?: ComponentType<{ className?: string }>;
  /** Se ausente, a ação aparece sempre que `extraAction` for passado. Usada
   *  quando a visibilidade depende de um campo só disponível depois do fetch
   *  (ex.: status de triagem do card), não de algo que o chamador já sabia
   *  de antemão. */
  isVisible?: (detail: CardDetailData) => boolean;
  onClick: (detail: CardDetailData) => void;
}

export interface CardPeekDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Título mostrado no cabeçalho do dialog enquanto carrega e nos estados de erro. */
  title: string;
  fetchDetail: () => Promise<CardDetailData>;
  /** false pula a chamada de API e já mostra "não disponível" — usado quando
   *  o chamador já sabe de antemão que o card não existe mais (ex.: chat
   *  share.is_available === false, sem precisar bater na API pra confirmar). */
  isAvailable?: boolean;
  /** Texto customizado pro estado "sem acesso" — default é genérico. */
  forbiddenMessage?: string;
  /** Ação extra no cabeçalho, ao lado de "Abrir no board" (ex.: "Ver na conversa"). */
  extraAction?: CardPeekExtraAction;
  /** true quando aberto por cima de outro diálogo modal (ex.: gaveta de
   *  arquivados) — sem isso o clique no card conta como "fora" da gaveta. */
  modal?: boolean;
  /** Esconde "Abrir no board"/"Ver na Triagem" — card arquivado não aparece no board. */
  hideBoardLink?: boolean;
  /** Linha extra abaixo do título (ex.: "Arquivado em …"). */
  subtitle?: string;
}

type LoadState = 'loading' | 'forbidden' | 'not_found' | 'error' | 'idle';

const PRIORITY_STYLES: Record<string, { label: string; classes: string }> = {
  low: { label: 'Baixa', classes: 'bg-slate-100 dark:bg-white/[0.06] text-slate-700 dark:text-slate-300' },
  medium: { label: 'Média', classes: 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:text-amber-300' },
  high: { label: 'Alta', classes: 'bg-orange-100 dark:bg-orange-500/15 text-orange-800 dark:text-orange-300' },
  urgent: { label: 'Urgente', classes: 'bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300' },
};

function displayName(u: UserMini | null): string {
  if (!u) return 'Usuário removido';
  return u.full_name?.trim() || u.username || 'Usuário';
}

function initials(u: UserMini | null): string {
  if (!u) return '??';
  const parts = displayName(u).split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase() || '??';
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `há ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours}h`;
  return `há ${Math.floor(hours / 24)}d`;
}

export function CardPeekDialog({
  open, onOpenChange, title, fetchDetail, isAvailable = true, forbiddenMessage, extraAction,
  modal = false, hideBoardLink = false, subtitle,
}: CardPeekDialogProps) {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>('loading');
  const [detail, setDetail] = useState<CardDetailData | null>(null);

  useEffect(() => {
    if (!open) return;
    setDetail(null);

    if (!isAvailable) {
      setState('not_found');
      return;
    }

    let cancelled = false;
    setState('loading');
    fetchDetail()
      .then((data) => {
        if (cancelled) return;
        setDetail(data);
        setState('idle');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) setState('forbidden');
        else if (err instanceof ApiError && err.status === 404) setState('not_found');
        else setState('error');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAvailable]);

  const priority = detail?.priority ? PRIORITY_STYLES[detail.priority] : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={modal}>
      <DialogContent mainArea className="!max-w-[1200px] h-[88vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-5 py-4 border-b border-slate-200 dark:border-white/[0.08] shrink-0 space-y-2">
          <div className="flex items-start justify-between gap-3 pr-8">
            <DialogTitle className="flex items-center gap-2 text-base font-semibold min-w-0">
              <Layout className="size-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
              <span className="truncate">{title}</span>
            </DialogTitle>
            {state === 'idle' && detail && (
              <div className="flex items-center gap-2 flex-shrink-0">
                {extraAction && (!extraAction.isVisible || extraAction.isVisible(detail)) && (
                  <Button variant="default" size="sm" onClick={() => extraAction.onClick(detail)}>
                    {extraAction.icon && <extraAction.icon className="size-3.5" />}
                    {extraAction.label}
                  </Button>
                )}
                {hideBoardLink ? null : detail.is_triage_board ? (
                  // O card em si vive no board de Triagem — /board/{uuid} (genérico,
                  // BoardCompleto) até abre o mesmo board, mas sem as ações
                  // especializadas de dispatch/return/validar que só existem na
                  // rota própria (TriagemKanban). Vai direto pra lá, no próprio card.
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onOpenChange(false);
                      navigate(`/boards/triagem?card=${detail.id}`);
                    }}
                  >
                    <ArrowUpCircle className="size-3.5" />
                    Ver na Triagem
                  </Button>
                ) : detail.triage_dispatch_origin ? (
                  // Card espelhado (mirror_group) pode viver em mais de um board —
                  // "Abrir no board" seria ambíguo sem um seletor. A Triagem é sempre
                  // um board único e o conteúdo (checklist/comentários/anexos) já é
                  // compartilhado entre todos os espelhos, então é o destino certo.
                  <Button
                    variant="outline"
                    size="sm"
                    title="Abrir card espelhado na Triagem"
                    onClick={() => {
                      onOpenChange(false);
                      navigate(`/boards/triagem?card=${detail.triage_dispatch_origin!.id}`);
                    }}
                  >
                    <ArrowUpCircle className="size-3.5" />
                    Ver na Triagem
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onOpenChange(false);
                      navigate(`/board/${detail.board}`);
                    }}
                  >
                    <ExternalLink className="size-3.5" />
                    Abrir no board
                  </Button>
                )}
              </div>
            )}
          </div>
          {subtitle && (
            <p className="text-xs text-slate-500 dark:text-slate-400" data-testid="card-peek-subtitle">{subtitle}</p>
          )}
          {state === 'idle' && detail && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="outline" className="text-xs font-normal gap-1">
                {detail.list_title}
              </Badge>
              {priority && (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium ${priority.classes}`}>
                  <Flag className="size-3.5" />
                  {priority.label}
                </span>
              )}
              {detail.due_date && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 dark:bg-white/[0.06] text-xs text-slate-700 dark:text-slate-300">
                  <CalendarClock className="size-3.5" />
                  Vence em {new Date(detail.due_date).toLocaleDateString('pt-BR')}
                </span>
              )}
            </div>
          )}
        </DialogHeader>

        {state === 'loading' && (
          <div className="flex flex-1 items-center justify-center text-slate-400 dark:text-slate-500">
            <Loader2 className="size-5 animate-spin" />
          </div>
        )}

        {state === 'forbidden' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center px-4">
            <Lock className="size-6 text-slate-400 dark:text-slate-500" />
            <p className="text-sm text-slate-600 dark:text-slate-400">Você não tem acesso a este card.</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {forbiddenMessage ?? 'Peça acesso ao setor deste card para visualizar os detalhes.'}
            </p>
          </div>
        )}

        {state === 'not_found' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center px-4">
            <AlertTriangle className="size-6 text-slate-400 dark:text-slate-500" />
            <p className="text-sm text-slate-600 dark:text-slate-400">Este card não está mais disponível.</p>
          </div>
        )}

        {state === 'error' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center px-4">
            <AlertTriangle className="size-6 text-red-400 dark:text-red-400" />
            <p className="text-sm text-slate-600 dark:text-slate-400">Não foi possível carregar este card. Tente novamente.</p>
          </div>
        )}

        {state === 'idle' && detail && (
          // Pela largura do card: estreito (< 48rem) empilha a lateral embaixo; largo, duas colunas.
          <div className="@container flex-1 min-h-0 overflow-y-auto">
          <div className="flex flex-col @3xl:flex-row @3xl:h-full">
            <div className="@3xl:flex-1 @3xl:min-w-0 @3xl:overflow-y-auto @3xl:border-r border-slate-200 dark:border-white/[0.08] px-5 py-4">
              <div className="space-y-5 pb-1">
                {detail.description && (
                  <section className="space-y-2">
                    <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                      <AlignLeft className="size-4 text-slate-500 dark:text-slate-400" />
                      <h3 className="text-sm font-semibold">Descrição</h3>
                    </div>
                    <p className="pl-6 text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap break-words">
                      {renderBoldText(detail.description)}
                    </p>
                  </section>
                )}

                {detail.checklists.map((checklist) => {
                  const total = checklist.items.length;
                  const completed = checklist.items.filter((i) => i.is_completed).length;
                  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
                  return (
                    <section key={checklist.id} className="space-y-2.5">
                      <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                        <CheckSquare className="size-4 text-slate-500 dark:text-slate-400" />
                        <h3 className="text-sm font-semibold">{checklist.title}</h3>
                        <span className="text-xs text-slate-500 dark:text-slate-400">{completed}/{total}</span>
                      </div>
                      <div className="h-1 bg-slate-100 dark:bg-white/[0.08] rounded-full overflow-hidden ml-6">
                        <div className="h-full bg-indigo-500 transition-all" style={{ width: `${percentage}%` }} />
                      </div>
                      <ul className="space-y-0.5 pl-6">
                        {checklist.items.map((item) => (
                          <li key={item.id} className="flex items-center gap-2.5 py-0.5">
                            {item.is_completed
                              ? <SquareCheck className="size-4 flex-shrink-0 text-indigo-500 dark:text-indigo-400" />
                              : <Square className="size-4 flex-shrink-0 text-slate-400 dark:text-slate-500" />}
                            <span className={`text-sm ${item.is_completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-300'}`}>
                              {item.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </section>
                  );
                })}

                {detail.attachments.length > 0 && (
                  <section className="space-y-2">
                    <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                      <Paperclip className="size-4 text-slate-500 dark:text-slate-400" />
                      <h3 className="text-sm font-semibold">Anexos</h3>
                      <span className="text-xs text-slate-500 dark:text-slate-400">{detail.attachments.length}</span>
                    </div>
                    <ul className="pl-6 space-y-1">
                      {detail.attachments.map((att) => (
                        <li key={att.id} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Paperclip className="size-3.5 flex-shrink-0 text-slate-400 dark:text-slate-500" />
                          <span className="truncate">{att.name}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </div>
            </div>

            <aside className="border-t border-slate-200 dark:border-white/[0.08] @3xl:border-t-0 @3xl:w-[38%] @3xl:min-w-[280px] @3xl:max-w-[380px] @3xl:shrink-0 @3xl:overflow-y-auto bg-slate-50 dark:bg-white/[0.02] px-5 py-4">
              <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 mb-3">
                <MessageSquareText className="size-4 text-slate-500 dark:text-slate-400" />
                <h3 className="text-sm font-semibold">Comentários</h3>
                <span className="text-xs text-slate-500 dark:text-slate-400">{detail.comments.length}</span>
              </div>
              {detail.comments.length === 0 ? (
                <p className="text-sm text-slate-400 dark:text-slate-500">Nenhum comentário ainda.</p>
              ) : (
                <div className="space-y-3">
                  {detail.comments.map((comment) => (
                    <div key={comment.id} className="flex items-start gap-2.5">
                      <div className="size-7 rounded-full bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
                        {initials(comment.author_detail)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-2">
                          <span className="text-slate-700 dark:text-slate-200 font-medium">{displayName(comment.author_detail)}</span>
                          <span className="text-slate-400 dark:text-slate-500">{timeAgo(comment.created_at)}</span>
                        </div>
                        <div className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/[0.08] px-3 py-2 rounded-md text-sm text-slate-800 dark:text-slate-200 leading-relaxed whitespace-pre-wrap break-words">
                          {comment.content}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}